import React from 'react';
import { LogOut } from 'lucide-react';
import RobbieMark from './RobbieMark';
import usePresence from './usePresence';

/**
 * Robbie overlay — a short wait (one AI call, seconds to a couple of minutes)
 * over the region it blocks. A light scrim covers the host (give the host
 * `relative`); the centred card says what Robbie is on, the usual range and,
 * for longer waits, that the buyer may leave. Never elapsed time, never a
 * checklist or percentage. When `open` turns false it dissolves — the page
 * moves on by itself. An `error` replaces the card's content in place.
 */
export const LONG_WAIT_COPY = 'Taking a bit longer — still working';

export interface RobbieOverlayProps {
  open: boolean;
  /** What Robbie is doing, short: "Going through 9 components". */
  title: React.ReactNode;
  sub?: React.ReactNode;
  /** The usual range: "Usually under a minute". */
  usual?: React.ReactNode;
  /** Past the usual range: the pill reads LONG_WAIT_COPY instead. */
  long?: boolean;
  /** Footer line; `true` uses the default "You can leave this page" copy. */
  leave?: React.ReactNode | true;
  /** A CompactError (stacked) shown in the same card instead of the wait. */
  error?: React.ReactNode;
  className?: string;
}

const DEFAULT_LEAVE = "You can leave this page — I'll keep going.";

export const RobbieOverlay: React.FC<RobbieOverlayProps> = ({
  open,
  title,
  sub,
  usual,
  long = false,
  leave,
  error,
  className = '',
}) => {
  const { mounted, leaving } = usePresence(open);
  if (!mounted) return null;
  return (
    <div
      data-leaving={leaving || undefined}
      className={`absolute inset-0 z-40 flex items-center justify-center bg-canvas/75 p-s7 transition-opacity duration-200 ${
        leaving ? 'opacity-0' : 'opacity-100'
      } ${className}`}
    >
      <div
        role={error ? undefined : 'status'}
        aria-live="polite"
        aria-busy={!error}
        className="flex w-full max-w-[392px] flex-col items-center rounded-[16px] border border-robbie-border bg-surface px-[30px] pb-s7 pt-[26px] text-center shadow-wait-card motion-safe:animate-wait-in"
      >
        {error ?? (
          <>
            <RobbieMark state="working" size="lg" aria-label="Robbie is working" className="mb-s4" />
            <p className="text-[17px] font-semibold leading-snug text-ink">{title}</p>
            {sub && <p className="mt-s2 text-body-md text-ink-secondary">{sub}</p>}
            {(usual || long) && (
              <p
                // Past the range stays calm: same tone, new words — not a warning.
                className="mt-s5 inline-flex items-center gap-s2 rounded-round bg-robbie-bg px-s4 py-[5px] text-[12.5px] font-medium text-robbie-ink"
              >
                {long ? LONG_WAIT_COPY : usual}
              </p>
            )}
            {leave && (
              <p className="mt-s6 flex w-full items-center justify-center gap-s2 border-t border-line-divider-soft pt-s5 text-body-sm text-ink-secondary">
                <LogOut aria-hidden="true" className="h-[13px] w-[13px] shrink-0 text-ink-muted" />
                {leave === true ? DEFAULT_LEAVE : leave}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RobbieOverlay;
